// Settings.js — engineer and firm lists.
// Each discipline's Report Details picks one engineer and one firm from these
// lists; the pick fills the signature and firm block on that report.

import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { rosterFromUser, newId, EMPTY_ENGINEER, EMPTY_FIRM } from '../disciplines';
import '../App.css';

const ENGINEER_FIELDS = [
  { key: 'name',           label: 'Name',                    placeholder: 'e.g. Jane Smith, PE', wide: true },
  { key: 'license',        label: 'License Number',          placeholder: 'e.g. PE #12345' },
  { key: 'discipline',     label: 'Discipline of Practice',  placeholder: 'e.g. Structural' },
  { key: 'email',          label: 'Email',                   placeholder: 'e.g. jane@firm.com' },
  { key: 'phone',          label: 'Phone',                   placeholder: 'e.g. 954-555-1234' },
  { key: 'qualifications', label: 'Qualifications (printed where the form asks for them; Return starts a new line)', placeholder: 'e.g. PE #12345, Level II Thermographer #6789\nB.S. Electrical Engineering, 25 years building inspections', full: true, multiline: true },
];

const FIRM_FIELDS = [
  { key: 'name',    label: 'Firm Name', placeholder: 'e.g. Example Engineering, LLC', wide: true },
  { key: 'phone',   label: 'Phone',     placeholder: 'e.g. 954-555-1234' },
  { key: 'email',   label: 'Email',     placeholder: 'e.g. info@firm.com' },
  { key: 'address', label: 'Address',   placeholder: 'e.g. 123 Main Street, Fort Lauderdale, FL 33301', full: true },
];

const Settings = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [dirty, setDirty]         = useState(false);
  const [uploading, setUploading] = useState('');
  const [engineers, setEngineers] = useState([]);
  const [firms, setFirms]         = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const roster = rosterFromUser(userDoc.exists() ? userDoc.data() : {});
        setEngineers(roster.engineers);
        setFirms(roster.firms);
      } catch (err) {
        console.error('Error loading settings:', err);
      }
      setLoading(false);
    };
    load();
  }, [currentUser]);

  const edit = (setList) => (id, key, value) => {
    setList(list => list.map(x => (x.id === id ? { ...x, [key]: value } : x)));
    setDirty(true);
  };
  const editEngineer = edit(setEngineers);
  const editFirm     = edit(setFirms);

  const remove = (setList, label) => (id, name) => {
    if (!window.confirm(`Remove ${label} "${name || 'unnamed'}"? Reports that used it will ask you to pick again.`)) return;
    setList(list => list.filter(x => x.id !== id));
    setDirty(true);
  };

  const handleLogoUpload = (firmId) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(firmId);
    const storageRef = ref(storage, `logos/${currentUser.uid}/${firmId}-${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed', null, (err) => {
      console.error(err);
      setUploading('');
    }, async () => {
      const url = await getDownloadURL(task.snapshot.ref);
      editFirm(firmId, 'logoUrl', url);
      setUploading('');
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'users', currentUser.uid), { engineers, firms }, { merge: true });
      setSaved(true);
      setDirty(false);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Save failed: ' + err.message);
    }
    setSaving(false);
  };

  if (loading) return <div style={{ marginLeft: '220px', padding: '2rem' }}>Loading...</div>;

  const fieldsGrid = (fields, item, onEdit) => (
    <div style={styles.grid}>
      {fields.map(f => (
        <div key={f.key} style={{ ...styles.field, gridColumn: f.full ? '1 / -1' : f.wide ? 'span 2' : 'span 1' }}>
          <label style={styles.label} htmlFor={`${item.id}-${f.key}`}>{f.label}</label>
          {f.multiline
            ? <textarea id={`${item.id}-${f.key}`} rows={2} style={{ ...styles.input, resize: 'vertical' }} value={item[f.key] || ''} placeholder={f.placeholder}
                onChange={e => onEdit(item.id, f.key, e.target.value)} />
            : <input id={`${item.id}-${f.key}`} style={styles.input} value={item[f.key] || ''} placeholder={f.placeholder}
                onChange={e => onEdit(item.id, f.key, e.target.value)} />}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <Sidebar />
      <div style={styles.container}>
        <div style={styles.card}>

          <div style={styles.titleRow}>
            <h2 style={styles.title}>Settings</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {dirty && <span style={styles.unsaved}>Unsaved changes</span>}
              <button style={styles.saveButton} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save Settings'}
              </button>
            </div>
          </div>
          <p style={styles.hint}>
            Each report’s Project Details has an engineer dropdown and a firm dropdown. Picking one fills the signature and firm block on that report.
          </p>

          {/* ── Engineers ── */}
          <div style={styles.section}>
            <div style={styles.sectionHead}>
              <h3 style={styles.sectionTitle}>Engineers</h3>
              <button type="button" style={styles.addButton}
                onClick={() => { setEngineers(l => [...l, { ...EMPTY_ENGINEER, id: newId() }]); setDirty(true); }}>
                + Add Engineer
              </button>
            </div>
            {!engineers.length && <p style={styles.empty}>No engineers yet.</p>}
            {engineers.map(e => (
              <div key={e.id} style={styles.entry}>
                {fieldsGrid(ENGINEER_FIELDS, e, editEngineer)}
                <div style={styles.entryActions}>
                  <button type="button" style={styles.removeButton} onClick={() => remove(setEngineers, 'engineer')(e.id, e.name)}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          {/* ── Firms ── */}
          <div style={{ ...styles.section, borderBottom: 'none' }}>
            <div style={styles.sectionHead}>
              <h3 style={styles.sectionTitle}>Firms</h3>
              <button type="button" style={styles.addButton}
                onClick={() => { setFirms(l => [...l, { ...EMPTY_FIRM, id: newId() }]); setDirty(true); }}>
                + Add Firm
              </button>
            </div>
            {!firms.length && <p style={styles.empty}>No firms yet.</p>}
            {firms.map(f => (
              <div key={f.id} style={styles.entry}>
                {fieldsGrid(FIRM_FIELDS, f, editFirm)}
                <div style={styles.logoRow}>
                  {f.logoUrl
                    ? <img src={f.logoUrl} alt={`${f.name || 'Firm'} logo`} style={styles.logoPreview} />
                    : <div style={styles.logoPlaceholder}>No logo</div>}
                  <label style={styles.uploadLabel}>
                    {uploading === f.id ? 'Uploading...' : f.logoUrl ? 'Replace Logo' : 'Upload Logo'}
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload(f.id)} disabled={!!uploading} />
                  </label>
                  <span style={styles.logoHint}>Photo report footer. PNG, transparent background, 300px+ wide.</span>
                  <button type="button" style={{ ...styles.removeButton, marginLeft: 'auto' }} onClick={() => remove(setFirms, 'firm')(f.id, f.name)}>Remove</button>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container:       { marginLeft: '200px', padding: '2rem', paddingBottom: '5rem', display: 'flex', justifyContent: 'center' },
  card:            { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', width: '100%', maxWidth: '900px' },
  titleRow:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  title:           { margin: 0, color: '#112b49', fontSize: '1.5rem' },
  hint:            { color: '#4b5563', fontSize: '0.875rem', margin: '0 0 1.5rem' },
  unsaved:         { fontSize: '0.8rem', color: '#92400e', fontStyle: 'italic' },
  saveButton:      { padding: '0.5rem 1.5rem', backgroundColor: 'cornflowerblue', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' },
  section:         { marginBottom: '1.75rem', paddingBottom: '1.25rem', borderBottom: '1px solid #e5e7eb' },
  sectionHead:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  sectionTitle:    { margin: 0, fontSize: '1rem', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' },
  addButton:       { padding: '0.4rem 1rem', backgroundColor: '#112b49', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' },
  empty:           { color: '#6b7280', fontSize: '0.875rem', margin: '0 0 0.5rem' },
  entry:           { border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '0.75rem', backgroundColor: '#fcfcfd' },
  entryActions:    { display: 'flex', justifyContent: 'flex-end' },
  grid:            { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0 0.75rem' },
  field:           { display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.6rem', minWidth: 0 },
  label:           { fontSize: '0.8rem', fontWeight: '500', color: '#374151' },
  input:           { padding: '0.45rem 0.65rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.9rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  removeButton:    { padding: '0.35rem 0.9rem', backgroundColor: 'white', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' },
  logoRow:         { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.25rem' },
  logoPreview:     { height: '44px', maxWidth: '160px', objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '3px', backgroundColor: 'white' },
  logoPlaceholder: { height: '44px', width: '120px', border: '2px dashed #d1d5db', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '0.75rem' },
  uploadLabel:     { display: 'inline-block', padding: '0.4rem 1rem', backgroundColor: '#112b49', color: 'white', borderRadius: '5px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' },
  logoHint:        { fontSize: '0.75rem', color: '#6b7280' },
};

export default Settings;
